import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import AdminHeader from '../AdminHeader/AdminHeader';
import AdminSidebar from '../AdminSidebar/AdminSidebar';
import { ADMIN_MAIN_HEADER_OFFSET_CLASS } from '../../sections/AdminDashboard/shared/adminLayoutSpec';
import { AdminModuleAccessProvider } from '../../context/AdminModuleAccessContext';
import useAdminViewOnlyGuard from '../../Hooks/useAdminViewOnlyGuard';

const AdminLayoutContent = ({ children, hideHeader, hideSidebar, containerClass, headerContainerClass, isDashboard, isMessagesOrQuotesContentArea }) => {
  const contentRef = useRef(null);
  useAdminViewOnlyGuard(contentRef);

  return (
    <main className="App dashboard-panel font-inter bg-white pb-12 lg:pb-16 min-h-screen">
      {!hideHeader && <AdminHeader containerClass={headerContainerClass} />}
      <div className={`${hideSidebar ? 'lg:block' : 'lg:grid lg:grid-cols-[0fr,_1fr]'} lg:mx-auto ${containerClass} ${isDashboard ? 'max-w-[120rem]' : ''} gap-12 hidden-scrollbar ${hideHeader ? 'mt-0' : ADMIN_MAIN_HEADER_OFFSET_CLASS}`}>
        {!hideSidebar && (
          <div className="hidden lg:block">
            <AdminSidebar />
          </div>
        )}
        <div
          ref={contentRef}
          className={`lg:mx-auto w-full min-w-0 max-w-full ${
            isMessagesOrQuotesContentArea ? '-mb-10' : 'pb-10 lg:pb-12'
          }`}
        >
          {children}
        </div>
      </div>
    </main>
  );
};

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const isDashboard = location.pathname === '/admin/dashboard';
  const isCustomers = location.pathname === '/admin/users/customers' || location.pathname.startsWith('/admin/users/customers/');
  const isBusinesses = location.pathname === '/admin/users/businesses' || location.pathname.startsWith('/admin/users/businesses/');
  const isPosts = location.pathname === '/admin/posts' || location.pathname.startsWith('/admin/posts/');
  const isVerificationRoot = location.pathname === '/admin/verification/queue';
  const isComplianceArchiveRoot = location.pathname === '/admin/verification/archive';
  const isComplianceArchiveDetail = location.pathname.startsWith('/admin/verification/archive/') && location.pathname !== '/admin/verification/archive';
  const isComplianceRulebookRoot = location.pathname === '/admin/verification/rules';
  const isComplianceRulebookDetail = location.pathname.startsWith('/admin/verification/rules/') && location.pathname !== '/admin/verification/rules';
  const isComplianceReports = location.pathname === '/admin/verification/reports';
  const isVerificationDetails = location.pathname.startsWith('/admin/verification/') && 
    location.pathname !== '/admin/verification/queue' && 
    !location.pathname.startsWith('/admin/verification/archive') &&
    !location.pathname.startsWith('/admin/verification/rules') &&
    !location.pathname.startsWith('/admin/verification/reports');
  const isVerification = isVerificationRoot || isVerificationDetails;
  const isSecurityRoot = location.pathname === '/admin/security';
  const isSecurityDetails = location.pathname.startsWith('/admin/security/') && location.pathname !== '/admin/security';
  const isFraudRoot = location.pathname === '/admin/fraud/alerts' || location.pathname === '/admin/fraud/archive' || location.pathname === '/admin/fraud/restrictions' || location.pathname === '/admin/fraud/incidents';
  const isFraudCaseDetail = location.pathname.startsWith('/admin/fraud/alerts/') && location.pathname !== '/admin/fraud/alerts';
  const isFraudArchiveDetail = location.pathname.startsWith('/admin/fraud/archive/') && location.pathname !== '/admin/fraud/archive';
  const isFraudDetails = (location.pathname.startsWith('/admin/fraud/incidents/') && location.pathname !== '/admin/fraud/incidents') || isFraudCaseDetail || isFraudArchiveDetail || (location.pathname.startsWith('/admin/fraud/restrictions/') && location.pathname !== '/admin/fraud/restrictions');
  const isSecurity = isSecurityRoot || isSecurityDetails || isFraudRoot || isFraudDetails;
  const isRestrictionDetail = location.pathname.startsWith('/admin/fraud/restrictions/') && location.pathname !== '/admin/fraud/restrictions';
  const isReviewsRoot =
    location.pathname === '/admin/reviews' ||
    location.pathname === '/admin/reviews/all' ||
    location.pathname === '/admin/reviews/pending' ||
    location.pathname === '/admin/reviews/flagged';
  const isReviewDetails =
    location.pathname.startsWith('/admin/reviews/') &&
    !['/admin/reviews/all', '/admin/reviews/pending', '/admin/reviews/flagged'].includes(location.pathname);
  const isReviews = isReviewsRoot || isReviewDetails;
  const isCustomerDetails = location.pathname.startsWith('/admin/users/customers/') && location.pathname !== '/admin/users/customers';
  const isBusinessDetails = location.pathname.startsWith('/admin/users/businesses/') && location.pathname !== '/admin/users/businesses';
  const postSubPages = ['/admin/posts/all', '/admin/posts/standard', '/admin/posts/featured', '/admin/posts/pending', '/admin/posts/flagged'];
  const isPostDetails = location.pathname.startsWith('/admin/posts/') && 
    location.pathname !== '/admin/posts' && 
    !postSubPages.includes(location.pathname);
  const isFinanceRoot =
    location.pathname === '/admin/finance/transactions' ||
    location.pathname === '/admin/billing/transactions';
  const isFinanceDetail =
    (location.pathname.startsWith('/admin/finance/transactions/') &&
      location.pathname !== '/admin/finance/transactions') ||
    (location.pathname.startsWith('/admin/billing/transactions/') &&
      location.pathname !== '/admin/billing/transactions') ||
    (location.pathname.startsWith('/admin/billing/subscriptions/') &&
      location.pathname !== '/admin/billing/subscriptions');
  const isFinanceReports =
    location.pathname === '/admin/finance/reports' ||
    location.pathname === '/admin/billing/reports';
  const isFinance = isFinanceRoot || isFinanceDetail || isFinanceReports;
  const isModeration = location.pathname.startsWith('/admin/moderation');
  const isModerationDetail = location.pathname.startsWith('/admin/moderation/queue/') && location.pathname !== '/admin/moderation/queue';
  const isWorkflowDetail = location.pathname.startsWith('/admin/moderation/workflows/') && location.pathname !== '/admin/moderation/workflows';
  const isAutoFlagDetail = location.pathname.startsWith('/admin/moderation/violations/auto-flag/');
  const isViolationDetail = location.pathname.startsWith('/admin/moderation/violations/violation/');
  const isViolationCodeDetail = location.pathname.startsWith('/admin/moderation/violations/code/');
  const isModerationRuleEditor = location.pathname.startsWith('/admin/moderation/rules/') && location.pathname !== '/admin/moderation/rules';
  const isManualCaseDetail = location.pathname.startsWith('/admin/moderation/manual/') && location.pathname !== '/admin/moderation/manual';
  const isMessagingDetail = location.pathname.startsWith('/admin/communication/all/') && location.pathname !== '/admin/communication/all';
  const isQuotesDetail =
    (location.pathname.startsWith('/admin/quotes/all/') && location.pathname !== '/admin/quotes/all') ||
    location.pathname.startsWith('/admin/quotes/group/');
  const isMessagingPage = location.pathname.startsWith('/admin/communication');
  const isQuotesPage = location.pathname.startsWith('/admin/quotes');
  const isMessagesOrQuotesContentArea = isMessagingPage || isQuotesPage;
  const isSupportTickets = location.pathname.startsWith('/admin/support/tickets');
  const isTicketDetail = location.pathname.startsWith('/admin/support/tickets/') && location.pathname !== '/admin/support/tickets';
  const isRoutingRulesRoot = location.pathname === '/admin/support/routing';
  const isRoutingRuleDetail = location.pathname.startsWith('/admin/support/routing/') && location.pathname !== '/admin/support/routing';
  const isRoutingRules = isRoutingRulesRoot || isRoutingRuleDetail;
  const isKnowledgeBaseRoot = location.pathname === '/admin/support/kb';
  const isKnowledgeBaseDetail = location.pathname.startsWith('/admin/support/kb/') && location.pathname !== '/admin/support/kb';
  const isKnowledgeBase = isKnowledgeBaseRoot || isKnowledgeBaseDetail;
  const isFeatureToggles = location.pathname === '/admin/settings/features';
  const isUnifiedAdminSettings =
    location.pathname === '/admin/settings/general' ||
    location.pathname === '/admin/settings/platform-controls' ||
    location.pathname === '/admin/settings/security' ||
    location.pathname === '/admin/settings/alerts' ||
    location.pathname === '/admin/settings/data-requests' ||
    location.pathname === '/admin/settings/data-recovery';
  const isSecurityLog = location.pathname === '/admin/settings/security-log';
  const pathnameNorm = (location.pathname || '/').replace(/\/+$/, '') || '/';
  const isTeamMembersPage = pathnameNorm.startsWith('/admin/settings/team-members');
  const isTeamMembersListRoot = pathnameNorm === '/admin/settings/team-members';
  const isTeamMembersAddPage = /\/admin\/settings\/team-members\/new$/i.test(pathnameNorm);
  const isTeamMembersEditPage = /\/admin\/settings\/team-members\/[^/]+\/edit$/i.test(pathnameNorm);
  const isTeamMembersActionPage =
    /\/admin\/settings\/team-members\/[^/]+\/(reset-password|activate|deactivate)$/i.test(
      pathnameNorm,
    );
  const isTeamMembersDetailPage =
    isTeamMembersPage &&
    !isTeamMembersListRoot &&
    !isTeamMembersAddPage &&
    !isTeamMembersEditPage &&
    !isTeamMembersActionPage;
  const isRbacAssignPage = pathnameNorm === '/admin/settings/rbac/assign';
  const isRbacAssignmentEditPage = /^\/admin\/settings\/rbac\/assignment\/[^/]+\/edit$/i.test(pathnameNorm);
  const isRbacRoleViewPage =
    /^\/admin\/settings\/rbac\/[^/]+$/i.test(pathnameNorm) &&
    pathnameNorm !== '/admin/settings/rbac/assign' &&
    pathnameNorm !== '/admin/settings/rbac/assignments' &&
    pathnameNorm !== '/admin/settings/rbac/roles';
  const isRolesPermissions = pathnameNorm.startsWith('/admin/settings/rbac');
  const isIntegrationsConfig =
    location.pathname === '/admin/integrations' || location.pathname === '/admin/settings/integrations';
  const isSettingsHistory = location.pathname === '/admin/settings/history';
  const isBackupRecovery = location.pathname === '/admin/settings/recovery';
  const isAuditChangeLog = location.pathname === '/admin/settings/audit' || location.pathname.startsWith('/admin/settings/audit/');
  const isDataExport = location.pathname === '/admin/settings/data';
  const isNotificationLog = location.pathname === '/admin/settings/notification-log' || location.pathname.startsWith('/admin/settings/notification-log/');
  const isSystemLogs = location.pathname === '/admin/system-logs' || location.pathname === '/admin/settings/system-logs';
  const isPrivacyPolicyEdit = location.pathname === '/admin/content/legal/privacy-policy';
  const isTermsConditionsEdit = location.pathname === '/admin/content/legal/terms-conditions';
  const isBlogPublished = location.pathname === '/admin/content/blog/published';
  const isBlogDraft = location.pathname === '/admin/content/blog/draft';
  const isBlogDeleted = location.pathname === '/admin/content/blog/deleted';
  const isBlogPages = isBlogPublished || isBlogDraft || isBlogDeleted;
  const isPlatformAnalytics = location.pathname.startsWith('/admin/analytics');
  const isReportsPage =
    location.pathname === '/admin/reports' || location.pathname.startsWith('/admin/reports/');
  const isConfigurationValidation = location.pathname.startsWith('/admin/configuration/validation');
  const isConfigurationValidationRoot = location.pathname === '/admin/configuration/validation';
  const isConfigurationValidationDetail = location.pathname.startsWith('/admin/configuration/validation/') && location.pathname !== '/admin/configuration/validation';
  const isConfigurationWorkflow = location.pathname.startsWith('/admin/configuration/workflow');
  const isConfigurationWorkflowRoot = location.pathname === '/admin/configuration/workflow';
  const isConfigurationWorkflowDetail = location.pathname.startsWith('/admin/configuration/workflow/') && location.pathname !== '/admin/configuration/workflow';
  const isTradeCategories = location.pathname.startsWith('/admin/trade-categories');
  const isTradeCategoriesRoot = location.pathname === '/admin/trade-categories';
  const isTradeCategoriesDetail = location.pathname.startsWith('/admin/trade-categories/') && location.pathname !== '/admin/trade-categories';
  const isTradeCategoryAddEdit =
    location.pathname === '/admin/trade-categories/add' ||
    /\/admin\/trade-categories\/[^/]+\/edit$/.test(location.pathname);
  const isComplianceItemAddEdit =
    location.pathname === '/admin/licences-badges-trade-tickets/add' ||
    /\/admin\/licences-badges-trade-tickets\/[^/]+\/edit$/.test(location.pathname);
  const isConfigurationAddEdit =
    /^\/admin\/configuration\/[^/]+\/add$/.test(location.pathname) ||
    /^\/admin\/configuration\/[^/]+\/[^/]+\/edit$/.test(location.pathname);
  const isKeywords = location.pathname.startsWith('/admin/keywords');
  const isKeywordsRoot = location.pathname === '/admin/keywords';
  const isKeywordsDetail = location.pathname.startsWith('/admin/keywords/') && location.pathname !== '/admin/keywords';
  const isKeywordsAddEdit =
    location.pathname === '/admin/keywords/add' ||
    /\/admin\/keywords\/[^/]+\/(edit|view)$/.test(location.pathname);
  const isFormInventory = location.pathname.startsWith('/admin/form-inventory');
  const isFormInventoryDetail =
    location.pathname.startsWith('/admin/form-inventory/') &&
    location.pathname !== '/admin/form-inventory';
  const isVerifications = location.pathname === '/admin/verifications';
  const isAppealsCasesRoot = location.pathname === '/admin/appeals';
  const isAppealDetail =
    location.pathname.startsWith('/admin/appeals/') && location.pathname !== '/admin/appeals';
  const isAppealsCases = isAppealsCasesRoot || isAppealDetail;
  const isComplianceItemsSource = location.pathname.startsWith('/admin/licences-badges-trade-tickets');
  const isComplianceItemsSourceRoot = location.pathname === '/admin/licences-badges-trade-tickets';
  const isComplianceItemsSourceDetail =
    location.pathname.startsWith('/admin/licences-badges-trade-tickets/') &&
    location.pathname !== '/admin/licences-badges-trade-tickets';
  const isConfigurationKeywords = location.pathname.startsWith('/admin/configuration/keywords');
  const isConfigurationKeywordsRoot = location.pathname === '/admin/configuration/keywords';
  const isConfigurationKeywordsDetail = location.pathname.startsWith('/admin/configuration/keywords/') && location.pathname !== '/admin/configuration/keywords';
  const isKeywordsSection = isKeywords || isConfigurationKeywords;
  const isConfigurationFormInventory = location.pathname.startsWith('/admin/configuration/form-inventory');
  const isConfigurationFormInventoryDetail =
    location.pathname.startsWith('/admin/configuration/form-inventory/') &&
    location.pathname !== '/admin/configuration/form-inventory';
  const isConfigurationVerifications = location.pathname === '/admin/configuration/verifications';
  const isPricingPlans = location.pathname.startsWith('/admin/pricing-plans');
  const isPricingPlansRoot = location.pathname === '/admin/pricing-plans';
  const isPricingPlansDetail = location.pathname.startsWith('/admin/pricing-plans/') && location.pathname !== '/admin/pricing-plans';
  const isCompactAddEditPage =
    isTradeCategoryAddEdit ||
    isKeywordsAddEdit ||
    isComplianceItemAddEdit ||
    isConfigurationAddEdit ||
    isPricingPlansDetail;
  const isConfigurationPlans = location.pathname.startsWith('/admin/configuration/plans');
  const isConfigurationPlansRoot = location.pathname === '/admin/configuration/plans';
  const isConfigurationPlansDetail = location.pathname.startsWith('/admin/configuration/plans/') && location.pathname !== '/admin/configuration/plans';
  const isConfigurationContentControl = location.pathname.startsWith('/admin/configuration/content-control');
  const isConfigurationContentControlRoot = location.pathname === '/admin/configuration/content-control';
  const isConfigurationContentControlDetail = location.pathname.startsWith('/admin/configuration/content-control/') && location.pathname !== '/admin/configuration/content-control';
  const isConfigurationFlag = location.pathname.startsWith('/admin/configuration/flag');
  const isConfigurationFlagRoot = location.pathname === '/admin/configuration/flag';
  const isConfigurationFlagDetail =
    location.pathname.startsWith('/admin/configuration/flag/') && location.pathname !== '/admin/configuration/flag';
  const isConfigurationPublishing = location.pathname.startsWith('/admin/configuration/publishing');
  const isConfigurationPublishingRoot = location.pathname === '/admin/configuration/publishing';
  const isConfigurationPublishingDetail = location.pathname.startsWith('/admin/configuration/publishing/') && location.pathname !== '/admin/configuration/publishing';
  const isConfigurationSearchMatching = location.pathname.startsWith('/admin/configuration/search-matching');
  const isConfigurationSearchMatchingRoot = location.pathname === '/admin/configuration/search-matching';
  const isConfigurationSearchMatchingDetail =
    location.pathname.startsWith('/admin/configuration/search-matching/') &&
    location.pathname !== '/admin/configuration/search-matching';
  const isConfigurationQuoteMatchingDistribution = location.pathname.startsWith('/admin/configuration/quote-matching-distribution');
  const isConfigurationQuoteMatchingDistributionDetail =
    location.pathname.startsWith('/admin/configuration/quote-matching-distribution/') &&
    location.pathname !== '/admin/configuration/quote-matching-distribution';
  const isConfigurationAppeals = location.pathname.startsWith('/admin/configuration/appeals');
  const isConfigurationAppealsRoot = location.pathname === '/admin/configuration/appeals';
  const isConfigurationAppealsTest = /\/admin\/configuration\/appeals\/[^/]+\/test$/.test(location.pathname);
  const isConfigurationAppealsDetail =
    location.pathname.startsWith('/admin/configuration/appeals/') &&
    location.pathname !== '/admin/configuration/appeals' &&
    !isConfigurationAppealsTest;
  const isConfigurationPermissionsAccess = location.pathname.startsWith('/admin/configuration/permissions-access');
  const isConfigurationPermissionsAccessRoot = location.pathname === '/admin/configuration/permissions-access';
  const isConfigurationPermissionsAccessDetail =
    location.pathname.startsWith('/admin/configuration/permissions-access/') &&
    location.pathname !== '/admin/configuration/permissions-access';
  const isConfigurationDocuments = location.pathname.startsWith('/admin/configuration/documents');
  const isConfigurationDocumentsRoot = location.pathname === '/admin/configuration/documents';
  const isConfigurationDocumentsDetail = isConfigurationDocuments && !isConfigurationDocumentsRoot;
  const isConfigurationLimits = location.pathname.startsWith('/admin/configuration/limits');
  const isConfigurationLimitsRoot = location.pathname === '/admin/configuration/limits';
  const isConfigurationLimitsDetail = location.pathname.startsWith('/admin/configuration/limits/') && location.pathname !== '/admin/configuration/limits';
  const isConfigurationCaseActions = location.pathname.startsWith('/admin/configuration/case-actions');
  const isConfigurationCaseActionsRoot = location.pathname === '/admin/configuration/case-actions';
  const isConfigurationCaseActionsDetail = location.pathname.startsWith('/admin/configuration/case-actions/') && location.pathname !== '/admin/configuration/case-actions';
  const isConfigurationSupportTicketRoot = location.pathname === '/admin/configuration/support-ticket';
  const isConfigurationSupportTicketDetail =
    location.pathname.startsWith('/admin/configuration/support-ticket/') &&
    location.pathname !== '/admin/configuration/support-ticket';
  const isConfigurationSupportTicket =
    isConfigurationSupportTicketRoot || isConfigurationSupportTicketDetail;
  const isConfigurationAutomation = location.pathname.startsWith('/admin/configuration/automation');
  const isConfigurationAutomationRoot = location.pathname === '/admin/configuration/automation';
  const isConfigurationAutomationDetail =
    location.pathname.startsWith('/admin/configuration/automation/') && location.pathname !== '/admin/configuration/automation';
  const isConfigurationLifecycle = location.pathname.startsWith('/admin/configuration/lifecycle');
  const isConfigurationLifecycleRoot = location.pathname === '/admin/configuration/lifecycle';
  const isConfigurationLifecycleDetail =
    location.pathname.startsWith('/admin/configuration/lifecycle/') && location.pathname !== '/admin/configuration/lifecycle';
  const isConfigurationRetention = location.pathname.startsWith('/admin/configuration/retention');
  const isConfigurationRetentionRoot = location.pathname === '/admin/configuration/retention';
  const isConfigurationRetentionDetail =
    location.pathname.startsWith('/admin/configuration/retention/') && location.pathname !== '/admin/configuration/retention';
  const isConfigurationNotifications = location.pathname.startsWith('/admin/configuration/notifications');
  const isConfigurationNotificationsRoot = location.pathname === '/admin/configuration/notifications';
  const isConfigurationNotificationsDetail = location.pathname.startsWith('/admin/configuration/notifications/') && location.pathname !== '/admin/configuration/notifications';
  const hideHeader = isPrivacyPolicyEdit || isTermsConditionsEdit;
  const hideSidebar = isCustomers || isBusinesses || isPosts || isReviews || isFinance || isVerification || isSecurity || isModeration || isMessagingPage || isQuotesPage || isComplianceArchiveRoot || isComplianceArchiveDetail || isComplianceRulebookRoot || isComplianceRulebookDetail || isComplianceReports || isSupportTickets || isRoutingRules || isKnowledgeBase || isFeatureToggles || isUnifiedAdminSettings || isTeamMembersPage || isRolesPermissions || isIntegrationsConfig || isSettingsHistory || isBackupRecovery || isAuditChangeLog || isDataExport || isNotificationLog || isSecurityLog || isSystemLogs || isPrivacyPolicyEdit || isTermsConditionsEdit || isBlogPages || isPlatformAnalytics || isReportsPage || isConfigurationValidation || isConfigurationWorkflow || isTradeCategories || isKeywordsSection || isFormInventory || isVerifications || isAppealsCases || isComplianceItemsSource || isPricingPlans || isConfigurationPlans || isConfigurationContentControl || isConfigurationFlag || isConfigurationPublishing || isConfigurationSearchMatching || isConfigurationQuoteMatchingDistribution || isConfigurationAppeals || isConfigurationPermissionsAccess || isConfigurationDocuments || isConfigurationLimits || isConfigurationCaseActions || isConfigurationSupportTicket || isConfigurationAutomation || isConfigurationLifecycle || isConfigurationRetention || isConfigurationNotifications || isConfigurationFormInventory || isConfigurationVerifications;
  const isConfigurationWideListRoot =
    isSettingsHistory ||
    isSystemLogs ||
    isConfigurationDocumentsRoot;
  const isMainContainerDetailPage =
    isCustomerDetails ||
    isBusinessDetails ||
    isPostDetails ||
    isModerationDetail ||
    isReviewDetails ||
    isMessagingDetail ||
    isQuotesDetail ||
    isVerificationDetails ||
    isComplianceArchiveDetail ||
    isFraudCaseDetail ||
    isFraudArchiveDetail ||
    isTicketDetail ||
    isAppealDetail ||
    isFinanceDetail;
  const isConfigurationDetailPage =
    isConfigurationValidationDetail ||
    isConfigurationWorkflowDetail ||
    isTradeCategoriesDetail ||
    isKeywordsDetail ||
    isComplianceItemsSourceDetail ||
    isConfigurationKeywordsDetail ||
    isPricingPlansDetail ||
    isConfigurationPlansDetail ||
    isConfigurationContentControlDetail ||
    isConfigurationFlagDetail ||
    isConfigurationPublishingDetail ||
    isConfigurationSearchMatchingDetail ||
    isConfigurationQuoteMatchingDistributionDetail ||
    isConfigurationAppealsDetail ||
    isConfigurationAppealsTest ||
    isConfigurationPermissionsAccessDetail ||
    isConfigurationDocumentsDetail ||
    isConfigurationLimitsDetail ||
    isConfigurationCaseActionsDetail ||
    isConfigurationSupportTicketDetail ||
    isConfigurationNotificationsDetail ||
    isConfigurationAutomationDetail ||
    isConfigurationLifecycleDetail ||
    isConfigurationRetentionDetail ||
    isFormInventoryDetail ||
    isConfigurationFormInventoryDetail;
  const isSmallWidthDetailPage =
    isSecurityDetails ||
    (isFraudDetails && !isFraudCaseDetail && !isFraudArchiveDetail) ||
    isWorkflowDetail ||
    isAutoFlagDetail ||
    isViolationDetail ||
    isViolationCodeDetail ||
    isModerationRuleEditor ||
    isManualCaseDetail ||
    isComplianceRulebookDetail ||
    isRoutingRuleDetail ||
    isKnowledgeBaseDetail ||
    isFeatureToggles ||
    isUnifiedAdminSettings ||
    isIntegrationsConfig ||
    isBackupRecovery ||
    isAuditChangeLog ||
    isDataExport ||
    isNotificationLog ||
    isSecurityLog ||
    isPrivacyPolicyEdit ||
    isTermsConditionsEdit ||
    isBlogPages;
  const containerClass =
    isCompactAddEditPage
      ? 'tp-container max-w-[25rem] !px-0'
      : isMainContainerDetailPage
      ? 'tp-container-main'
      : isRbacAssignPage || isRbacAssignmentEditPage
      ? 'tp-container-small'
      : (isPlatformAnalytics || isReportsPage)
      ? 'tp-container max-w-[1920px]'
      : isConfigurationWideListRoot
      ? 'tp-container max-w-[1920px]'
      : isConfigurationDetailPage
      ? 'tp-container max-w-3xl !px-0'
      : isSmallWidthDetailPage
      ? 'tp-container-small'
      : (isBusinesses || postSubPages.includes(location.pathname) || isComplianceArchiveRoot || isComplianceReports || isFinanceReports) && !isVerificationRoot && !isComplianceRulebookRoot && !isFinanceRoot && !isFinanceDetail
      ? 'tp-container max-w-[1920px]'
      : isTeamMembersAddPage ||
          isTeamMembersEditPage ||
          isTeamMembersDetailPage ||
          isTeamMembersActionPage
      ? 'tp-container max-w-field-page-md !px-0'
      : isRbacRoleViewPage
      ? 'tp-container max-w-3xl'
      : 'tp-container';

  const headerContainerClass = `${containerClass} ${isDashboard ? 'max-w-[120rem]' : ''}`.trim();

  return (
    <AdminModuleAccessProvider>
      <AdminLayoutContent
        hideHeader={hideHeader}
        hideSidebar={hideSidebar}
        containerClass={containerClass}
        headerContainerClass={headerContainerClass}
        isDashboard={isDashboard}
        isMessagesOrQuotesContentArea={isMessagesOrQuotesContentArea}
      >
        {children}
      </AdminLayoutContent>
    </AdminModuleAccessProvider>
  );
};

export default AdminLayout;
